// src/pages/firm/label/LabelTemplateDialog.tsx

import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Save, Plus, X, Tag, Copy } from "lucide-react";
import { IconTextButton } from "../../../components/ButtonComponents";
import { LabelTemplate, LabelTemplatePayload } from "../../../types/label";
import { useLabelStore } from "../../../store/labelStore";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    template: LabelTemplate | null;
    isCopy?: boolean;
    onSave: (data: LabelTemplatePayload) => void;
}

export const LabelTemplateDialog: React.FC<Props> = ({ isOpen, onClose, template, isCopy, onSave }) => {
    const { getCategories } = useLabelStore();
    const [categories, setCategories] = useState<{code: string; name: string}[]>([]);

    const [formData, setFormData] = useState<LabelTemplatePayload>({
        code: "",
        name: "",
        description: ""
    });

    useEffect(() => {
        getCategories().then(res => setCategories(res));
    }, []);

    useEffect(() => {
        if (isOpen) {
            if (template) {
                const randomSuffix = Math.floor(Math.random() * 10000);
                setFormData({
                    code: isCopy ? `${template.code}_KOPYA_${randomSuffix}` : (template.code || ""),
                    category: template.category || (template.code as any),
                    name: isCopy ? `${template.name} (Kopya ${randomSuffix})` : (template.name || ""),
                    description: template.description || ""
                });
            } else {
                setFormData({ 
                    code: "", 
                    category: undefined,
                    name: "", 
                    description: "" 
                });
            }
        }
    }, [template, isOpen, isCopy]);

    const handleSubmit = () => {
        if (!formData.code || !formData.name) {
            return;
        }

        if (template && !isCopy) {
            const { code, ...updatePayload } = formData;
            onSave(updatePayload as any); 
        } else {
            onSave(formData);
        }
    };

    return (
        <Dialog 
            header={
                <div className="flex items-center gap-2">
                    <Tag className="w-5 h-5 text-indigo-600" />
                    <span>{template ? (isCopy ? "Şablonu Kopyala" : "Şablon Bilgilerini Düzenle") : "Yeni Etiket Şablonu Tanımla"}</span>
                </div>
            }
            visible={isOpen} 
            onHide={onClose}
            className="w-full md:w-4/12 lg:w-3/12"
            modal
            footer={
                <div className="flex justify-end gap-2">
                    <IconTextButton onClick={onClose} icon={X} className="btn-cancel">İptal</IconTextButton>
                    <IconTextButton onClick={handleSubmit} icon={template && !isCopy ? Save : (isCopy ? Copy : Plus)} className="btn-new">
                        {template && !isCopy ? "Güncelle" : (isCopy ? "Kopyala" : "Şablon Oluştur")}
                    </IconTextButton>
                </div>
            }
        >
            <div className="flex flex-col gap-4 p-2">
                {/* Şablon Kategorisi */}
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold">Şablon Kategorisi (İş Süreci)</label>
                    <Dropdown
                        value={formData.category} 
                        options={categories}
                        optionLabel="name"
                        optionValue="code"
                        onChange={(e) => setFormData({...formData, category: e.value})} 
                        placeholder="Kategori Seçin"
                        className="w-full"
                    />
                </div>

                {/* Şablon Sistem Kodu */}
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold">Şablon Sistem Kodu</label>
                    <InputText 
                        value={formData.code} 
                        onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase().replace(/\s+/g, '_')})} 
                        placeholder="Örn: PICKING_OZELETİKET"
                        disabled={!!template && !isCopy}
                    />
                    <small className="text-gray-400">Backend entegrasyonunda kullanılacak benzersiz kod.</small>
                </div>

                {/* Şablon Adı */}
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold">Şablon Görünen Adı</label>
                    <InputText 
                        value={formData.name} 
                        onChange={(e) => setFormData({...formData, name: e.target.value})} 
                        placeholder="Örn: Kargo ve Sevkiyat Etiketi"
                    />
                </div>
                
                {/* Açıklama */}
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold">Açıklama</label>
                    <InputText 
                        value={formData.description || ""} 
                        onChange={(e) => setFormData({...formData, description: e.target.value})} 
                        placeholder="Şablon kullanım amacı..."
                    />
                </div>
            </div>
        </Dialog>
    );
};